import { type FocusEvent, forwardRef, useState } from "react";
import { classNames } from "../../classNames";
import { useFieldContext } from "../Field/Field";
import { onlyDigits, type OtpBox, otpBoxes } from "./otp";

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Called once all digits are present (typed, pasted or autofilled). */
  onComplete?: (value: string) => void;
  length?: number;
  invalid?: boolean;
  disabled?: boolean;
  autoFocus?: boolean;
  /** Accessible name when not wrapped in a Field. */
  label?: string;
  shaking?: boolean;
}

/**
 * Six separate boxes over a single real <input>, so typing, paste, SMS/OTP
 * autofill and password managers all work natively.
 */
export const OtpInput = forwardRef<HTMLInputElement, OtpInputProps>(function OtpInput(
  { value, onChange, onComplete, length = 6, invalid, disabled, autoFocus, label = "6-digit code", shaking },
  ref,
) {
  const field = useFieldContext();
  const [focused, setFocused] = useState(false);
  const isInvalid = invalid || field?.invalid;

  function onFocus(e: FocusEvent<HTMLInputElement>): void {
    setFocused(true);
    // Keep the caret after the last digit, wherever the box was clicked.
    const el = e.currentTarget;
    requestAnimationFrame(() => el.setSelectionRange(el.value.length, el.value.length));
  }

  return (
    <div className={classNames("nb-otp", focused ? "is-focused" : null, isInvalid ? "is-invalid" : null, disabled ? "is-disabled" : null, shaking ? "is-shaking" : null)}>
      <input
        ref={ref}
        id={field?.id}
        className="nb-otp-input"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        pattern="[0-9]*"
        maxLength={length}
        aria-label={field ? undefined : label}
        aria-invalid={isInvalid || undefined}
        aria-describedby={field?.describedBy}
        value={value}
        disabled={disabled}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onBlur={() => setFocused(false)}
        onChange={(e) => {
          const next = onlyDigits(e.target.value, length);
          onChange(next);
          if (next.length === length && value.length !== length) onComplete?.(next);
        }}
      />
      {otpBoxes(value, length).map((box, i) => (
        <OtpDigit key={i} box={box} focused={focused} />
      ))}
    </div>
  );
});

function OtpDigit({ box, focused }: { box: OtpBox; focused: boolean }): React.JSX.Element {
  return (
    <div aria-hidden="true" className={`nb-otp-box${box.char ? " is-filled" : ""}${box.active ? " is-active" : ""}`}>
      {box.char ?? (focused && box.active ? <span className="nb-otp-caret" /> : null)}
    </div>
  );
}
